import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useEffect, Component, type ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null; errorInfo: string }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: "" };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("React Error Boundary caught:", error, info.componentStack);
    this.setState({ errorInfo: info.componentStack || "" });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
            <h1 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h1>
            <p className="text-sm text-slate-500 mb-4">
              {String(this.state.error?.message || "An unexpected error occurred.")}
            </p>
            {this.state.errorInfo && (
              <details className="text-left mb-4 bg-slate-50 rounded-lg p-3 max-h-48 overflow-auto">
                <summary className="text-xs font-medium text-slate-600 cursor-pointer">Technical Details</summary>
                <pre className="text-[10px] text-slate-500 mt-2 whitespace-pre-wrap break-words">
                  {String(this.state.errorInfo)}
                </pre>
              </details>
            )}
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: "" });
                window.location.href = "/";
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Pages
import NotFound from "@/pages/not-found";
import Login from "@/pages/login";
import Home from "@/pages/home";
import Dashboard from "@/pages/dashboard";
import Statements from "@/pages/statements";
import StatementDetail from "@/pages/statement-detail";
import Receipts from "@/pages/receipts";
import MatchFailures from "@/pages/match-failures";
import ReceiptReview from "@/pages/receipt-review";
import Settings from "@/pages/settings";
import UsersPage from "@/pages/users";
import SupplierTemplatesPage from "@/pages/supplier-templates";
import NominalTemplatesPage from "@/pages/nominal-templates";
import NominalAssignmentPage from "@/pages/nominal-assignment";
import ContractsPage from "@/pages/contracts";
import ContractDetailPage from "@/pages/contract-detail";
import PrepaymentsPage from "@/pages/prepayments";
import CashReceiptsPage from "@/pages/cash-receipts";
import CashReceiptsGuidePage from "@/pages/cash-receipts-guide";
import CashReceiptsRulesPage from "@/pages/cash-receipts-rules";
import CashReceiptsAdjustmentsPage from "@/pages/cash-receipts-adjustments";
import CashReceiptsDailyBatchPage from "@/pages/cash-receipts-daily-batch";
import CashReceiptsProformaPage from "@/pages/cash-receipts-proforma";
import ProformaEvidencePage from "@/pages/proforma-evidence";
import StripeRefundsPage from "@/pages/stripe-refunds";
import CourierInvoicesPage from "@/pages/courier-invoices";
import CollectionPodsPage from "@/pages/collection-pods";
import AuditLogPage from "@/pages/audit-log";
import EmailLogPage from "@/pages/email-log";
import CreditChecksPage from "@/pages/credit-checks";
import UpcomingPaymentsPage from "@/pages/upcoming-payments";
import CostPriceChecksPage from "@/pages/cost-price-checks";
import ProfitLossPage from "@/pages/profit-loss";
import BankReconciliationPage from "@/pages/bank-reconciliation";
import { PAGE_GROUPS, hasGroupAccess, hasGroupAdmin, type PageGroupKey } from "@/lib/pagePermissions";

const queryClient = new QueryClient();

// Auth Guard Component
// `groupKey` gates the route by page-group permission — admins bypass;
// other users must have the key in their pagePermissions.
// `adminOnly` further restricts the route to admins (used inside Expenses
// for everything except /receipts).
function PrivateRoute({ component: Component, groupKey, adminOnly, ...rest }: any) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();

  const group = groupKey ? PAGE_GROUPS.find((g) => g.key === groupKey) : undefined;
  const groupOk = !groupKey ? true : group ? hasGroupAccess(user, group) : false;
  // `adminOnly` means "needs admin powers inside this group" — satisfied
  // by a global admin OR a per-group admin (pageAdminPermissions).
  const adminOk = !adminOnly || (!!groupKey && hasGroupAdmin(user, groupKey));
  const allowed = groupOk && adminOk;

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      setLocation("/login");
    } else if (!allowed) {
      // In "view as user" mode (or for actual users without permission)
      // gated pages bounce back to the home grid so the preview is leak-free.
      setLocation("/");
    }
  }, [isLoading, isAuthenticated, allowed, setLocation]);

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin"></div></div>;
  }

  if (!isAuthenticated) return null;
  if (!allowed) return null;
  return <Component {...rest} />;
}

// Tiny helpers so each route reads like `gated("expenses", Statements)`
// or `gatedAdmin("expenses", Statements)` for admin-only pages inside a group.
const gated = (groupKey: PageGroupKey, Component: any) =>
  () => <PrivateRoute component={Component} groupKey={groupKey} />;
const gatedAdmin = (groupKey: PageGroupKey, Component: any) =>
  () => <PrivateRoute component={Component} groupKey={groupKey} adminOnly />;

function Router() {
  return (
    <Switch>
      {/* Public Route — Clerk owns sign-in/up. */}
      <Route path="/login" component={Login} />

      {/* Protected Routes */}
      <Route path="/" component={() => <PrivateRoute component={Home} />} />
      {/* Expenses: only /receipts is open to non-admin users (upload). */}
      <Route path="/expenses" component={gatedAdmin("expenses", Dashboard)} />
      <Route path="/upcoming-payments" component={gated("expenses", UpcomingPaymentsPage)} />
      <Route path="/statements" component={gatedAdmin("expenses", Statements)} />
      <Route path="/statements/:id" component={gatedAdmin("expenses", StatementDetail)} />
      <Route path="/receipts" component={gated("expenses", Receipts)} />
      <Route path="/receipts/:id" component={gatedAdmin("expenses", ReceiptReview)} />
      <Route path="/match-failures" component={gatedAdmin("expenses", MatchFailures)} />
      <Route path="/supplier-templates" component={gatedAdmin("expenses", SupplierTemplatesPage)} />
      <Route path="/nominal-templates" component={gatedAdmin("expenses", NominalTemplatesPage)} />
      <Route path="/cost-price-checks" component={gatedAdmin("expenses", CostPriceChecksPage)} />
      <Route path="/profit-loss" component={gated("profit_loss", ProfitLossPage)} />
      <Route path="/statements/:id/nominals" component={gatedAdmin("expenses", NominalAssignmentPage)} />
      {/* Contracts + prepayments are open to every authenticated user;
          delete + prepayment-settings stay admin-gated server-side. */}
      <Route path="/contracts" component={() => <PrivateRoute component={ContractsPage} />} />
      <Route path="/contracts/:id" component={() => <PrivateRoute component={ContractDetailPage} />} />
      <Route path="/prepayments" component={() => <PrivateRoute component={PrepaymentsPage} />} />
      <Route path="/cash-receipts" component={gated("cash_receipts", CashReceiptsPage)} />
      <Route path="/cash-receipts/guide" component={gated("cash_receipts", CashReceiptsGuidePage)} />
      <Route path="/cash-receipts/rules" component={gated("cash_receipts", CashReceiptsRulesPage)} />
      <Route path="/cash-receipts/proforma-payments" component={gated("cash_receipts", CashReceiptsProformaPage)} />
      <Route path="/cash-receipts/proforma-payments/:id/evidence/:kind/:recordId" component={gated("cash_receipts", ProformaEvidencePage)} />
      <Route path="/cash-receipts/adjustments" component={gatedAdmin("cash_receipts", CashReceiptsAdjustmentsPage)} />
      <Route path="/cash-receipts/daily-batch" component={gated("cash_receipts", CashReceiptsDailyBatchPage)} />
      <Route path="/stripe-refunds" component={gatedAdmin("cash_receipts", StripeRefundsPage)} />
      <Route path="/credit-checks" component={gated("cash_receipts", CreditChecksPage)} />
      <Route path="/bank-reconciliation" component={gated("bank_reconciliation", BankReconciliationPage)} />
      <Route path="/courier-invoices" component={gated("courier_invoices", CourierInvoicesPage)} />
      {/* Collection PODs is open to every authenticated user (ungated). */}
      <Route path="/collection-pods" component={() => <PrivateRoute component={CollectionPodsPage} />} />
      <Route path="/users" component={gated("users_settings", UsersPage)} />
      <Route path="/settings" component={gated("users_settings", Settings)} />
      {/* Audit log is super-admin only — gated by users_settings at the
          page-group layer plus an internal isSuperAdmin check on the page
          itself (and the API enforces requireSuperAdmin). */}
      <Route path="/audit-log" component={gated("users_settings", AuditLogPage)} />
      <Route path="/email-log" component={() => <PrivateRoute component={EmailLogPage} />} />

      {/* Fallback */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <Router />
          </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
